import { importProvidersFrom, NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { LucideAngularModule, Monitor } from 'lucide-angular';
import { AuthGuard } from './core/guards/auth.guard';
import { UnauthorizedGuard } from './core/guards/unauthorized.guard';
import { MobileBlockedGuard } from './core/guards/mobile-blocked.guard';
import { GuideFlowConfigGuard } from './core/guards/guide-flow-config.guard';
import { PreAttendanceGuard } from './core/guards/pre-attendance.guard';
import { SetoresHospitalaresGuard } from './core/guards/setores-hospitalares.guard';
import { UnauthorizedPageComponent } from './features/auth/pages/unauthorized-page/unauthorized-page.component';
import { InternalServerErrorPageComponent } from './features/auth/pages/internal-server-error-page/internal-server-error-page.component';

// Exported so the route table itself can be asserted (`app-routing.module.spec.ts`)
// — same precedent as `features/exam-cost/exam-cost-routing.module.ts`.
export const routes: Routes = [
  {
    path: '',
    redirectTo: '/login',
    pathMatch: 'full'
  },
  {
    path: 'login',
    loadChildren: () => import('./features/auth/auth.module').then(m => m.AuthModule)
  },
  {
    path: 'auth',
    loadChildren: () => import('./features/auth/auth.module').then(m => m.AuthModule)
  },
  {
    path: 'unauthorized',
    component: UnauthorizedPageComponent,
    canActivate: [UnauthorizedGuard]
  },
  {
    path: 'internal-server-error',
    component: InternalServerErrorPageComponent
  },
  {
    path: 'desktop-only',
    loadComponent: () =>
      import('./features/desktop-only/desktop-only-page.component').then(
        (m) => m.DesktopOnlyPageComponent
      ),
    providers: [importProvidersFrom(LucideAngularModule.pick({ Monitor }))]
  },
  // Public legal pages — reachable without authentication from the login
  // footer, sidebar footer (desktop) and menu-drawer footer (mobile/tablet).
  // Lazy-loaded standalone components. See docs/LEGAL-PAGES.md for the
  // content-refresh pipeline.
  {
    path: 'legal/privacidade',
    loadComponent: () =>
      import('./features/legal/pages/privacy-policy-page/privacy-policy-page.component').then(
        (m) => m.PrivacyPolicyPageComponent
      )
  },
  {
    path: 'legal/termos',
    loadComponent: () =>
      import('./features/legal/pages/terms-of-use-page/terms-of-use-page.component').then(
        (m) => m.TermsOfUsePageComponent
      )
  },
  { path: 'privacidade', redirectTo: '/legal/privacidade', pathMatch: 'full' },
  { path: 'privacy', redirectTo: '/legal/privacidade', pathMatch: 'full' },
  { path: 'termos', redirectTo: '/legal/termos', pathMatch: 'full' },
  { path: 'terms', redirectTo: '/legal/termos', pathMatch: 'full' },
  {
    path: 'home',
    loadChildren: () => import('./features/home/home.module').then(m => m.HomeModule),
    canActivate: [AuthGuard]
  },
  {
    path: 'avd-loading',
    loadChildren: () => import('./features/avd-loading/avd-loading.module').then(m => m.AvdLoadingModule)
  },
  { path: 'inicio', redirectTo: 'home', pathMatch: 'full' },
  {
    path: 'attendance',
    loadChildren: () => import('./features/attendance/attendance.module').then(m => m.AttendanceModule),
    canActivate: [AuthGuard, MobileBlockedGuard],
    data: { desktopOnly: true }
  },
  { path: 'fila-coleta', redirectTo: 'attendance/collection-queue', pathMatch: 'full' },
  {
    // US #61578 — the area of the Vacinas module: the shell with the global Local picker, where the
    // operational screens of the Épico 60162 fit as children. A desktop work area like `attendance`
    // and `patients` ([D-P3]). NO menu entry yet ([D-P13]): the first operational screen (Feature 61547)
    // seeds its own subitem — a subitem for a screen that does not exist would be a dead menu item.
    path: 'vaccines',
    loadChildren: () => import('./features/vaccines/vaccines.module').then(m => m.VaccinesModule),
    canActivate: [AuthGuard, MobileBlockedGuard],
    data: { desktopOnly: true }
  },
  { path: 'vacinas', redirectTo: 'vaccines', pathMatch: 'full' },
  {
    path: 'pre-attendance',
    loadChildren: () => import('./features/pre-attendance/pre-attendance.module').then(m => m.PreAttendanceModule),
    canActivate: [AuthGuard, MobileBlockedGuard, PreAttendanceGuard],
    data: { desktopOnly: true }
  },
  { path: 'pre-atendimento', redirectTo: 'pre-attendance' },
  { path: 'pre-atendimento-inteligente', redirectTo: 'pre-attendance' },
  {
    path: 'godmode',
    loadChildren: () => import('./features/godmode/godmode.module').then(m => m.GodmodeModule),
    canActivate: [AuthGuard, MobileBlockedGuard],
    data: { desktopOnly: true }
  },
  {
    path: 'printing',
    loadChildren: () => import('./features/printing/printing.module').then(m => m.PrintingModule),
    canActivate: [AuthGuard, MobileBlockedGuard],
    data: { desktopOnly: true }
  },
  { path: 'configuracao-impressao', redirectTo: 'printing', pathMatch: 'full' },
  {
    path: 'custo-exame',
    loadChildren: () => import('./features/exam-cost/exam-cost.module').then(m => m.ExamCostModule),
    canActivate: [AuthGuard, MobileBlockedGuard],
    data: { desktopOnly: true }
  },
  {
    path: 'punctuality-monitoring',
    loadChildren: () => import('./features/punctuality-monitoring/punctuality-monitoring.module').then(m => m.PunctualityMonitoringModule),
    canActivate: [AuthGuard]
  },
  {
    // US #61499 — Faturamento → Controle de Caixa is a routed page; the Pagamentos ficha opens as a
    // modal over it. Only `AuthGuard` gates the route, and that is deliberate: AC5 / D-03 require the
    // page to OPEN and show the access-denied message where the grid would be, and the API answers 403
    // by the same permission key on its own. Do NOT add a permission guard here.
    path: 'cash-control',
    loadComponent: () =>
      import('./features/cash-control/pages/cash-control-page/cash-control-page.component').then(
        (m) => m.CashControlPageComponent
      ),
    canActivate: [AuthGuard]
  },
  {
    // US #62593 — Faturamento → Faturamento SUS is a routed page (WI 62593), in the mould of /cash-control above.
    // The path is the one the menu seed ships (`usuarios/063_MenuFaturamentoFaturamentoSus.sql`), so no menu
    // migration is needed. Only `AuthGuard` gates the route, and that is deliberate: the page OPENS and shows the
    // access-denied message where the filters would be, and the API answers 403 by the same `FATURAMENTO_SUS` key on
    // its own. Do NOT add a permission guard here.
    path: 'sus-billing',
    loadComponent: () =>
      import('./features/sus-billing/pages/sus-billing-page/sus-billing-page.component').then(
        (m) => m.SusBillingPageComponent
      ),
    canActivate: [AuthGuard]
  },
  {
    // US #62981 — Recepção › Central de atendimento is a routed page. The menu seed ships `/central-atendimento`,
    // which `BACKEND_ROUTE_TO_ANGULAR` translates to this path. Only `AuthGuard` gates it, deliberately: the search
    // asks for no permission of its own (AC13, RN-07), and the API answers the same way. Do NOT add a permission
    // guard here. The search is the empty-path child and the patient record mounts beside it. The
    // page's own icons are imported by the page (`AttendanceCenterIconsModule`), not provided here: a route-level
    // pick would be shadowed by the pick of the LayoutModule the page imports.
    path: 'attendance-center',
    canActivate: [AuthGuard],
    children: [
      {
        path: '',
        loadComponent: () =>
          import('./features/attendance-center/pages/attendance-center-page/attendance-center-page.component').then(
            (m) => m.AttendanceCenterPageComponent
          )
      },
      {
        // The patient record: no guard of its own either (AC13). The id is validated by the page, which answers
        // "Registro não encontrado." for one that cannot exist (E7) instead of a route that does not match.
        path: 'patients/:id',
        loadComponent: () =>
          import('./features/attendance-center/pages/patient-record-page/patient-record-page.component').then(
            (m) => m.PatientRecordPageComponent
          )
      }
    ]
  },
  { path: 'indicadores', redirectTo: 'punctuality-monitoring', pathMatch: 'full' },
  { path: 'monitoramento-pontualidade-resultados', redirectTo: 'punctuality-monitoring', pathMatch: 'full' },
  // TASK-60341: Cadastros → Estabelecimento de Saúde has NO route — it opens as
  // an in-app overlay hosted by LayoutComponent (HealthFacilitiesModalService),
  // reachable from the sidebar, mobile drawer and home quick-access by submodule
  // key. Removing the route also retired HealthFacilitiesGuard (the overlay is
  // gated by the same permission that hides the menu item — Inc 3).
  {
    // Cadastros → Setores Hospitalares. The path is what the menu seed already ships
    // (`usuarios/054_MenuGrupoCadastrosEstabelecimentoSaude.sql`), so no menu migration is needed.
    //
    // The permission guard sits on the CHILD (`path: ''`), one level BELOW `AuthGuard`: guards of the
    // same `canActivate` array run concurrently, and the denial toast would fire even while
    // `AuthGuard` is already refusing the navigation. Between route levels the sequence is real.
    path: 'hospital-sectors',
    canActivate: [AuthGuard],
    children: [
      {
        path: '',
        loadComponent: () =>
          import('./features/setores-hospitalares/pages/setores-hospitalares-page/setores-hospitalares-page.component').then(
            (m) => m.SetoresHospitalaresPageComponent
          ),
        canActivate: [SetoresHospitalaresGuard]
      }
    ]
  },
  {
    path: 'messages',
    loadChildren: () => import('./features/messages/messages.module').then(m => m.MessagesModule),
    canActivate: [AuthGuard]
  },
  { path: 'gestao-comunicacao-times', redirectTo: 'messages', pathMatch: 'full' },
  // WhatsApp attendance panel. AuthGuard only: no module permission gate (out of product scope).
  {
    path: 'whatsapp',
    loadChildren: () => import('./features/whatsapp/whatsapp.module').then(m => m.WhatsAppModule),
    canActivate: [AuthGuard]
  },
  {
    path: 'guide-flow',
    loadChildren: () => import('./features/guide-flow/guide-flow.module').then(m => m.GuideFlowModule),
    canActivate: [AuthGuard]
  },
  { path: 'auditoria-pre-faturamento', redirectTo: 'guide-flow', pathMatch: 'full' },
  {
    // Guide-flow stage configuration — adapted for mobile in Phase 6.6
    // (customer asked to allow editing the audit flow from a phone).
    // Stage cards stack vertically on phones, drag-and-drop reorder is
    // hidden (touch-unfriendly) but the up/down buttons still work.
    path: 'guide-flow-config',
    loadComponent: () =>
      import('./features/guide-flow-config/pages/guide-flow-config-page/guide-flow-config-page.component').then(
        (m) => m.GuideFlowConfigPageComponent
      ),
    canActivate: [AuthGuard, GuideFlowConfigGuard]
  },
  { path: 'cadastro-fluxo-auditoria-pre-faturamento', redirectTo: 'guide-flow-config', pathMatch: 'full' },
  {
    // US #61464 — route space for the Integrações menu folder. The folder's
    // catalogue row carries a NULL route on purpose: it opens the next-level
    // panel, it does not navigate. `/integrations` exists here only as the
    // parent of its single child.
    //
    // TECHNICAL NOTE — the absence of a permission gate is deliberate, not an
    // oversight. The team decided (US #61464 §8 D4) that access to an
    // integration is checked ON THE CLICK, not on the menu: every tenant sees
    // every integration, and the check lands with the functional screen in
    // US #61476. Until then the page is a placeholder, so there is nothing to
    // protect. Do NOT "fix" this by adding a guard or a permission key here —
    // that would silently contradict AC-07 of this story.
    path: 'integrations',
    canActivate: [AuthGuard],
    children: [
      {
        // `tasy` is a PARENT: its children are the screens of the integration.
        // The menu catalogue keeps `/integrations/tasy` (the menu is data, not
        // code), and the empty-path child sends it — and every old link — to
        // the single hospitals screen (US #62732 AC-01). Both destinations are
        // pinned in `app-routing.module.spec.ts`.
        //
        // The children inherit the ABSENCE of a permission gate from the parent,
        // and that absence stays deliberate: AC-12 of #61927 needs the API's
        // refusal to reach the user as a MESSAGE, which a guard would replace
        // with a route that never renders.
        path: 'tasy',
        children: [
          { path: '', redirectTo: 'hospitais', pathMatch: 'full' },
          {
            path: 'hospitais',
            loadComponent: () =>
              import(
                './features/integrations/tasy/pages/tasy-hospitals-page/tasy-hospitals-page.component'
              ).then((m) => m.TasyHospitalsPageComponent)
          },
          {
            // US #61933 — the documentation and prerequisites screen. The path is a
            // CONTRACT with US #61931, whose header action and shortcut point here.
            // Authentication is inherited from the `integrations` node; the screen
            // reads nothing, so it declares no guard of its own.
            path: 'documentacao',
            loadComponent: () =>
              import(
                './features/integrations/tasy/pages/tasy-documentation-page/tasy-documentation-page.component'
              ).then((m) => m.TasyDocumentationPageComponent)
          },
          {
            // US #62723 — the log of nonconformities of the hospital in context. The
            // path is a CONTRACT with the home page's shortcut and US #62724.
            // No guard of its own: the refusal of the API reaches the user as the
            // page's access-refused state, like the Hospitais grid.
            path: 'inconformidades',
            loadComponent: () =>
              import(
                './features/integrations/tasy/pages/tasy-nonconformity-log-page/tasy-nonconformity-log-page.component'
              ).then((m) => m.TasyNonconformityLogPageComponent)
          }
          // The hospital ficha has NO route (WI #62938 AC-02): it is a modal over `hospitais`, opened by
          // the list. An old link to `hospitais/novo` or `hospitais/:id` falls through to the `**` below.
        ]
      },
      // AC-14 — `/integrations` typed straight into the address bar lands on the
      // folder's only child. Without this entry the router does NOT backtrack to
      // the top-level `**`: an empty remainder under a matched parent is a
      // successful match with no child, so the navigation completes at
      // `/integrations` with nothing to render. A non-empty unknown remainder
      // does backtrack. Both destinations are pinned in
      // `app-routing.module.spec.ts`. Empty-path redirect goes last, after its
      // target, as in every feature routing module (`attendance`, `exam-cost`,
      // `guide-flow`, `messages`, `printing`, `punctuality-monitoring`).
      { path: '', redirectTo: 'tasy', pathMatch: 'full' }
    ]
  },
  {
    path: 'external/medicamentos',
    loadComponent: () =>
      import('./features/attendance/pages/external-medication-page/external-medication-page.component').then(
        (m) => m.ExternalMedicationPageComponent
      )
  },
  {
    path: '**',
    redirectTo: '/home',
    pathMatch: 'full'
  }
];

@NgModule({
  imports: [RouterModule.forRoot(routes, {
    enableTracing: false,
    scrollPositionRestoration: 'top',
    // Enables native scroll to `#fragment` targets so the legal pages'
    // table-of-contents links (e.g. `/legal/privacidade#sec5`) jump to the
    // correct heading. The 80px offset matches the legal shell's sticky
    // header height.
    anchorScrolling: 'enabled',
    scrollOffset: [0, 80]
  })],
  exports: [RouterModule]
})
export class AppRoutingModule { }
